import { WallShape } from "../types";
import { getWallControlPoint, quadraticBezierPoint } from "./bezier";
import { getWallDirectionAtEndpoint } from "./wallJunction";

export interface FloorPolygon {
  points: { x: number; y: number }[];
}

interface GraphNode {
  id: number;
  x: number;
  y: number;
}

interface HalfEdge {
  id: number;
  from: number;
  to: number;
  wall: WallShape;
  forward: boolean;
  twinId: number;
  nextId: number;
  departureAngle: number;
  visited: boolean;
}

const BEZIER_SAMPLES = 20;
const MIN_FACE_AREA = 100;

/**
 * If point (px, py) lies on the straight segment (x1,y1)→(x2,y2) strictly
 * between the endpoints (within `epsilon`), return the parameter t in
 * (0, 1). Otherwise null.
 */
const pointOnSegmentT = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  px: number,
  py: number,
  epsilon: number
): number | null => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return null;
  const t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  if (t <= 0 || t >= 1) return null;
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  const distSq = (projX - px) * (projX - px) + (projY - py) * (projY - py);
  if (distSq > epsilon * epsilon) return null;
  return t;
};

const isStraight = (wall: WallShape): boolean =>
  !wall.curvature || wall.curvature === 0;

/** Proper crossing of two straight segments: the intersection point when
 *  it lies strictly inside both, else null. */
const segmentCrossing = (
  a: WallShape,
  b: WallShape
): { x: number; y: number; ta: number; tb: number } | null => {
  const dax = a.x2 - a.x;
  const day = a.y2 - a.y;
  const dbx = b.x2 - b.x;
  const dby = b.y2 - b.y;
  const denominator = dax * dby - day * dbx;
  if (Math.abs(denominator) < 1e-9) return null;
  const ta = ((b.x - a.x) * dby - (b.y - a.y) * dbx) / denominator;
  const tb = ((b.x - a.x) * day - (b.y - a.y) * dax) / denominator;
  if (ta <= 0 || ta >= 1 || tb <= 0 || tb >= 1) return null;
  return { x: a.x + ta * dax, y: a.y + ta * day, ta, tb };
};

/**
 * Splits straight walls wherever the graph needs a node that the drawing
 * does not have: an endpoint of another wall touching (or nearly
 * touching, within `tolerance`) the interior, and two walls crossing each
 * other, as they do when a corner was drawn with an overshoot. Without
 * these nodes the face tracer cannot close the rooms on either side.
 * The touch point is projected onto the host so it stays straight; the
 * loose endpoint merges into that node in `buildNodes`.
 *
 * Curved walls are passed through unchanged: splitting a bezier would
 * need re-parameterising the curve.
 */
const splitWallsAtJunctions = (
  walls: WallShape[],
  tolerance: number
): WallShape[] => {
  const endpoints: { x: number; y: number }[] = [];
  const addEndpoint = (x: number, y: number) => {
    for (const ep of endpoints) {
      if (Math.hypot(ep.x - x, ep.y - y) <= tolerance) return;
    }
    endpoints.push({ x, y });
  };
  for (const w of walls) {
    addEndpoint(w.x, w.y);
    addEndpoint(w.x2, w.y2);
  }
  const isNearEndpoint = (x: number, y: number) =>
    endpoints.some((ep) => Math.hypot(ep.x - x, ep.y - y) <= tolerance);

  const cutsByWall = walls.map<{ t: number; x: number; y: number }[]>(() => []);
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    if (!isStraight(w)) continue;
    const dx = w.x2 - w.x;
    const dy = w.y2 - w.y;
    for (const ep of endpoints) {
      const t = pointOnSegmentT(w.x, w.y, w.x2, w.y2, ep.x, ep.y, tolerance);
      if (t !== null)
        cutsByWall[i].push({ t, x: w.x + t * dx, y: w.y + t * dy });
    }
    for (let j = i + 1; j < walls.length; j++) {
      const other = walls[j];
      if (!isStraight(other)) continue;
      const crossing = segmentCrossing(w, other);
      // A crossing next to an endpoint is a T-junction, handled above.
      if (!crossing || isNearEndpoint(crossing.x, crossing.y)) continue;
      cutsByWall[i].push({ t: crossing.ta, x: crossing.x, y: crossing.y });
      cutsByWall[j].push({ t: crossing.tb, x: crossing.x, y: crossing.y });
    }
  }

  const result: WallShape[] = [];
  walls.forEach((w, index) => {
    const cuts = cutsByWall[index];
    if (cuts.length === 0) {
      result.push(w);
      return;
    }
    cuts.sort((a, b) => a.t - b.t);
    let px = w.x;
    let py = w.y;
    cuts.forEach((c, i) => {
      result.push({
        ...w,
        id: `${w.id}-tj${i}`,
        x: px,
        y: py,
        x2: c.x,
        y2: c.y
      });
      px = c.x;
      py = c.y;
    });
    result.push({
      ...w,
      id: `${w.id}-tj${cuts.length}`,
      x: px,
      y: py,
      x2: w.x2,
      y2: w.y2
    });
  });
  return result;
};

/**
 * Drops walls that end in mid-air (an endpoint no other wall reaches),
 * repeatedly, so the stubs of an overshot corner or an unfinished wall do
 * not leave spikes in the room polygons. A stub can never close a room,
 * so nothing is lost.
 */
const pruneDanglingWalls = (
  walls: WallShape[],
  tolerance: number
): WallShape[] => {
  let current = walls;
  for (;;) {
    const { nodes, endpointToNode } = buildNodes(current, tolerance);
    const degree = new Array<number>(nodes.length).fill(0);
    current.forEach((_, wi) => {
      const start = endpointToNode.get(`${wi}-start`)!;
      const end = endpointToNode.get(`${wi}-end`)!;
      if (start === end) return;
      degree[start] += 1;
      degree[end] += 1;
    });
    const kept = current.filter((_, wi) => {
      const start = endpointToNode.get(`${wi}-start`)!;
      const end = endpointToNode.get(`${wi}-end`)!;
      return start !== end && degree[start] > 1 && degree[end] > 1;
    });
    if (kept.length === current.length) return kept;
    current = kept;
  }
};

/** How far apart two things may be and still count as joined: the
 *  thickest wall on the floor, since a gap narrower than the wall body
 *  is not visible. Never below one unit. */
const autoTolerance = (walls: WallShape[]): number =>
  Math.max(1, ...walls.map((wall) => wall.thickness || 0));

/**
 * Merge wall endpoints within epsilon into graph nodes.
 * Returns array of unique nodes and a mapping from (wallIndex, endpoint) to node index.
 */
const buildNodes = (
  walls: WallShape[],
  epsilon: number
): { nodes: GraphNode[]; endpointToNode: Map<string, number> } => {
  const eps2 = epsilon * epsilon;
  const nodes: GraphNode[] = [];
  const endpointToNode = new Map<string, number>();

  const findOrCreateNode = (x: number, y: number): number => {
    for (let i = 0; i < nodes.length; i++) {
      const dx = nodes[i].x - x;
      const dy = nodes[i].y - y;
      if (dx * dx + dy * dy <= eps2) {
        return i;
      }
    }
    const id = nodes.length;
    nodes.push({ id, x, y });
    return id;
  };

  for (let wi = 0; wi < walls.length; wi++) {
    const wall = walls[wi];
    const startNode = findOrCreateNode(wall.x, wall.y);
    const endNode = findOrCreateNode(wall.x2, wall.y2);
    endpointToNode.set(`${wi}-start`, startNode);
    endpointToNode.set(`${wi}-end`, endNode);
  }

  return { nodes, endpointToNode };
};

/**
 * Create two directed half-edges per wall (forward: start→end, backward: end→start).
 * Compute departure angle at each half-edge's "from" node.
 */
const buildHalfEdges = (
  walls: WallShape[],
  endpointToNode: Map<string, number>
): HalfEdge[] => {
  const halfEdges: HalfEdge[] = [];

  for (let wi = 0; wi < walls.length; wi++) {
    const wall = walls[wi];
    const fromNode = endpointToNode.get(`${wi}-start`)!;
    const toNode = endpointToNode.get(`${wi}-end`)!;

    // Skip zero-length walls (same node)
    if (fromNode === toNode) continue;

    const fwdId = halfEdges.length;
    const bwdId = fwdId + 1;

    // Forward: start→end. Direction leaving start = direction from start toward wall.
    const fwdDir = getWallDirectionAtEndpoint(wall, "start");
    const fwdAngle = Math.atan2(fwdDir.y, fwdDir.x);

    // Backward: end→start. Direction leaving end = direction from end toward wall.
    const bwdDir = getWallDirectionAtEndpoint(wall, "end");
    const bwdAngle = Math.atan2(bwdDir.y, bwdDir.x);

    halfEdges.push({
      id: fwdId,
      from: fromNode,
      to: toNode,
      wall,
      forward: true,
      twinId: bwdId,
      nextId: -1,
      departureAngle: fwdAngle,
      visited: false
    });

    halfEdges.push({
      id: bwdId,
      from: toNode,
      to: fromNode,
      wall,
      forward: false,
      twinId: fwdId,
      nextId: -1,
      departureAngle: bwdAngle,
      visited: false
    });
  }

  return halfEdges;
};

/**
 * Group half-edges by their "from" node, sorted CCW by departure angle.
 * Returns outgoing[nodeId] = sorted array of half-edge ids.
 */
const buildAdjacency = (
  halfEdges: HalfEdge[],
  nodeCount: number
): number[][] => {
  const outgoing: number[][] = Array.from({ length: nodeCount }, () => []);

  for (const he of halfEdges) {
    outgoing[he.from].push(he.id);
  }

  // Sort each node's outgoing edges by departure angle (CCW)
  for (let i = 0; i < nodeCount; i++) {
    outgoing[i].sort(
      (a, b) => halfEdges[a].departureAngle - halfEdges[b].departureAngle
    );
  }

  return outgoing;
};

/**
 * For each half-edge e arriving at node v, find its twin in v's outgoing list,
 * then take the previous entry (wrapping around). This is standard planar face linkage.
 */
const assignNextPointers = (
  halfEdges: HalfEdge[],
  outgoing: number[][]
): void => {
  for (const he of halfEdges) {
    const twin = halfEdges[he.twinId];
    const nodeOutgoing = outgoing[twin.from];

    // Find twin's position in the sorted outgoing list at the arrival node
    const twinIdx = nodeOutgoing.indexOf(twin.id);

    // Previous entry (wrap around) — this is the next half-edge in the face
    const prevIdx = twinIdx === 0 ? nodeOutgoing.length - 1 : twinIdx - 1;
    he.nextId = nodeOutgoing[prevIdx];
  }
};

/**
 * Trace faces by following nextId pointers from each unvisited half-edge.
 */
const traceFaces = (halfEdges: HalfEdge[]): number[][] => {
  const faces: number[][] = [];

  for (const he of halfEdges) {
    if (he.visited) continue;

    const face: number[] = [];
    let current = he.id;

    // Safety: limit iterations to avoid infinite loops
    const maxIter = halfEdges.length;
    let iter = 0;

    do {
      if (halfEdges[current].visited) break;
      halfEdges[current].visited = true;
      face.push(current);
      current = halfEdges[current].nextId;
      iter++;
    } while (current !== he.id && iter < maxIter);

    if (face.length >= 3) {
      faces.push(face);
    }
  }

  return faces;
};

/**
 * Compute signed area of a face using the shoelace formula.
 * In screen coordinates (y-down): positive = CW winding = interior room.
 */
const computeSignedArea = (
  faceNodeIds: number[],
  nodes: GraphNode[]
): number => {
  let area = 0;
  const n = faceNodeIds.length;
  for (let i = 0; i < n; i++) {
    const curr = nodes[faceNodeIds[i]];
    const next = nodes[faceNodeIds[(i + 1) % n]];
    area += curr.x * next.y - next.x * curr.y;
  }
  return area / 2;
};

/**
 * Generate the polygon points for a face, sampling bezier curves for curved walls.
 */
const generateFacePolygon = (
  face: number[],
  halfEdges: HalfEdge[],
  nodes: GraphNode[]
): FloorPolygon => {
  const points: { x: number; y: number }[] = [];

  for (const heId of face) {
    const he = halfEdges[heId];
    const wall = he.wall;
    const curvature = wall.curvature || 0;

    if (curvature === 0) {
      // Straight wall: just add the "from" node point
      const fromNode = nodes[he.from];
      points.push({ x: fromNode.x, y: fromNode.y });
    } else {
      // Curved wall: sample bezier points along centerline
      const curvatureCenter = wall.curvatureCenter ?? 0.5;
      const { cx, cy } = getWallControlPoint(
        wall.x,
        wall.y,
        wall.x2,
        wall.y2,
        curvature,
        curvatureCenter
      );

      if (he.forward) {
        // Sample from t=0 to t=1 (excluding last point, it's next edge's start)
        for (let i = 0; i < BEZIER_SAMPLES; i++) {
          const t = i / BEZIER_SAMPLES;
          const p = quadraticBezierPoint(
            wall.x,
            wall.y,
            cx,
            cy,
            wall.x2,
            wall.y2,
            t
          );
          points.push(p);
        }
      } else {
        // Sample from t=1 to t=0 (reversed direction)
        for (let i = 0; i < BEZIER_SAMPLES; i++) {
          const t = 1 - i / BEZIER_SAMPLES;
          const p = quadraticBezierPoint(
            wall.x,
            wall.y,
            cx,
            cy,
            wall.x2,
            wall.y2,
            t
          );
          points.push(p);
        }
      }
    }
  }

  return { points };
};

/**
 * Detect closed regions formed by walls using planar graph face-finding
 * and return white floor fill polygons for each interior room.
 *
 * Forgiving by default: endpoints within the wall thickness of each other
 * or of another wall are treated as joined, crossing walls are split at
 * the crossing, and dangling stubs are ignored, so a floor that looks
 * closed detects as closed even when the walls do not meet exactly. Pass
 * `epsilon` to use a fixed tolerance instead.
 */
export const computeWallFloorPolygons = (
  allWalls: WallShape[],
  epsilon?: number
): FloorPolygon[] => {
  const walls = allWalls.filter((wall) => !wall.excludeFromFloorFill);
  if (walls.length < 3) return [];
  const tolerance = epsilon ?? autoTolerance(walls);

  // 0. Split walls at T-junctions and crossings so every place walls
  //    touch becomes an explicit node in the graph, then drop the stubs
  //    that cannot take part in a room.
  const splitWalls = pruneDanglingWalls(
    splitWallsAtJunctions(walls, tolerance),
    tolerance
  );
  if (splitWalls.length < 3) return [];

  // 1. Build graph nodes by merging nearby endpoints
  const { nodes, endpointToNode } = buildNodes(splitWalls, tolerance);

  // 2. Create directed half-edges
  const halfEdges = buildHalfEdges(splitWalls, endpointToNode);
  if (halfEdges.length === 0) return [];

  // 3. Build adjacency (outgoing edges per node, sorted by angle)
  const outgoing = buildAdjacency(halfEdges, nodes.length);

  // 4. Assign next pointers for face traversal
  assignNextPointers(halfEdges, outgoing);

  // 5. Trace all faces
  const faces = traceFaces(halfEdges);

  // 6. Filter interior faces and generate polygons
  const polygons: FloorPolygon[] = [];

  for (const face of faces) {
    // Get node IDs for area calculation
    const faceNodeIds = face.map((heId) => halfEdges[heId].from);
    const area = computeSignedArea(faceNodeIds, nodes);

    // Keep only interior faces (positive signed area in screen coords, above min threshold)
    if (area > MIN_FACE_AREA) {
      polygons.push(generateFacePolygon(face, halfEdges, nodes));
    }
  }

  return polygons;
};
